import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Button,
} from '@mui/material';

const localDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const memberName = (member) =>
  member?.user_name ||
  [member?.user_details?.first_name, member?.user_details?.last_name]
    .filter(Boolean)
    .join(' ') ||
  `Employé #${member?.team_member_id}`;

const toLocalInput = (value, fallback) => {
  if (!value) return fallback;
  const date = new Date(value);
  return {
    date: localDate(date),
    time: `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes(),
    ).padStart(2, '0')}`,
  };
};

export const buildUnavailabilityPayload = ({
  salonId,
  teamMember,
  kind,
  isAllDay,
  startDate,
  endDate,
  startTime,
  endTime,
  reason,
}) => {
  const startsAt = new Date(`${startDate}T${isAllDay ? '00:00' : startTime}:00`);
  const endBase = new Date(`${endDate}T${isAllDay ? '00:00' : endTime}:00`);
  if (isAllDay) endBase.setDate(endBase.getDate() + 1);
  return {
    salon: Number(salonId),
    team_member: Number(teamMember),
    kind,
    starts_at: startsAt.toISOString(),
    ends_at: endBase.toISOString(),
    is_all_day: isAllDay,
    reason: reason.trim(),
  };
};

const EmployeeUnavailabilityForm = ({
  open,
  onClose,
  onSubmit,
  members,
  preselectedMember,
  initialValue,
  isLoading,
  salonId,
}) => {
  const today = useMemo(() => localDate(), []);
  const [teamMember, setTeamMember] = useState('');
  const [kind, setKind] = useState('leave');
  const [isAllDay, setIsAllDay] = useState(true);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const start = toLocalInput(initialValue?.starts_at, {
      date: today,
      time: '09:00',
    });
    const rawEnd = initialValue?.ends_at
      ? new Date(initialValue.ends_at)
      : null;
    if (rawEnd && initialValue?.is_all_day) rawEnd.setDate(rawEnd.getDate() - 1);
    const end = toLocalInput(rawEnd, { date: today, time: '18:00' });
    setTeamMember(
      initialValue?.team_member || preselectedMember?.team_member_id || '',
    );
    setKind(initialValue?.kind || 'leave');
    setIsAllDay(initialValue?.is_all_day ?? true);
    setStartDate(start.date);
    setEndDate(end.date);
    setStartTime(start.time);
    setEndTime(end.time);
    setReason(initialValue?.reason || '');
    setError('');
  }, [open, initialValue, preselectedMember, today]);

  const submit = async () => {
    try {
      const payload = buildUnavailabilityPayload({
        salonId,
        teamMember,
        kind,
        isAllDay,
        startDate,
        endDate,
        startTime,
        endTime,
        reason,
      });
      if (!teamMember) throw new Error('Sélectionnez un employé.');
      if (new Date(payload.ends_at) <= new Date(payload.starts_at)) {
        throw new Error('La fin doit être après le début.');
      }
      await onSubmit(payload);
    } catch (submitError) {
      setError(
        submitError?.data?.detail ||
          submitError?.message ||
          "Impossible d'enregistrer l'indisponibilité.",
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800, color: 'primary.dark' }}>
        {initialValue ? "Modifier l'indisponibilité" : 'Ajouter une indisponibilité'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth required>
            <InputLabel id="absence-employee-label">Employé</InputLabel>
            <Select
              labelId="absence-employee-label"
              label="Employé"
              value={teamMember}
              onChange={(event) => setTeamMember(event.target.value)}
            >
              {(members || []).map((member) => (
                <MenuItem key={member.team_member_id} value={member.team_member_id}>
                  {memberName(member)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="absence-kind-label">Type</InputLabel>
            <Select
              labelId="absence-kind-label"
              label="Type"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
            >
              <MenuItem value="leave">Congé</MenuItem>
              <MenuItem value="absence">Absence</MenuItem>
              <MenuItem value="other">Autre indisponibilité</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={isAllDay}
                onChange={(event) => setIsAllDay(event.target.checked)}
                inputProps={{ 'aria-label': 'Journée entière' }}
              />
            }
            label="Journée entière"
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Date de début"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="Date de fin"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Box>
          {!isAllDay && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Heure de début"
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="Heure de fin"
                type="time"
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
          )}
          <TextField
            label="Motif interne (facultatif)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline
            minRows={2}
            helperText="Ce motif ne sera jamais visible par les clientes."
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Fermer</Button>
        <Button variant="contained" onClick={submit} disabled={isLoading}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeUnavailabilityForm;
